import { Box } from '@mui/material'
import useIsEqual from 'hook/useIsEqual'
import PageContainer from 'layouts/PageContainer'
import { PositionHoof } from 'models'
import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ButtonUI } from 'ui/buttons/ButtonUI'
import ExitWarningModal from 'ui/modal/ExitWarningModal'
import Preloader from 'ui/Preloader'
import { TypographySecondary } from 'ui/typography/TypographySecondary'
import { leaveCheckupPage } from 'utils/checkupWindow'
import { useGetCowInfoByTagId } from '../../../hook/query/useGetCowInfoByTagId'
import { useSubmitCheckUpInfo } from './hooks/useSubmitCheckUpInfo'
import { defaultCheckupInfo } from './processes/checkup'
import DisposeRecommendedBlock from './ui/DisposeRecommendedSection'
import MainInfoSection from './ui/MainInfoSection'
import { TreatmentCard } from './ui/TreatmentCard/TreatmentCard'
import TrimSection from './ui/TrimSection'
import ViewHistorySection from './ui/ViewHistorySection'
import { isCheckupValid } from './utils/checkupValidation'

const CheckUpPage = () => {
	const { id } = useParams()
	const navigate = useNavigate()
	const [exitDialogOpen, setExitDialogOpen] = useState(false)

	const [isSubmitAttempted, setIsSubmitAttempted] = useState(false)
	const [selectedHoof, setSelectedHoof] = useState<PositionHoof | null>(null)

	const { cowInfo, checkupInfo, setCheckupInfo, isLoading, error } =
		useGetCowInfoByTagId({ tag_id: id })

	const isEqualState = useIsEqual(defaultCheckupInfo, checkupInfo)

	const { submitForm: submitCheckupData } = useSubmitCheckUpInfo({
		cowInfo,
		checkupInfo,
	})

	const [, sethasTreatmentError] = useState(false)

	const handleSaveCheckup = () => {
		setIsSubmitAttempted(true)
		if (!isCheckupValid(checkupInfo)) return
		submitCheckupData()
	}
	const canSave = useMemo(
		() =>
			(!isEqualState || Boolean(selectedHoof)) && isCheckupValid(checkupInfo),
		[checkupInfo, isEqualState, selectedHoof],
	)

	return (
		<PageContainer>
			{isLoading ? (
				<Preloader mt={20} />
			) : error ? (
				<TypographySecondary textAlign='center'>
					Произошла ошибка
				</TypographySecondary>
			) : (
				<>
					<MainInfoSection cowInfo={cowInfo} />
					<ViewHistorySection cowId={cowInfo.id} />
					<TreatmentCard
						checkupInfo={checkupInfo}
						setCheckupInfo={setCheckupInfo}
						isSubmitAttempted={isSubmitAttempted}
						selectedHoof={selectedHoof}
						setSelectedHoof={setSelectedHoof}
					/>
					<TrimSection
						trim={checkupInfo.trim}
						setCheckupInfo={setCheckupInfo}
					/>
					<DisposeRecommendedBlock
						disposeRecommended={checkupInfo.dispose_recommended}
						setCheckupInfo={setCheckupInfo}
						diagnosisInfo={checkupInfo.diagnosis}
						onValidationErrorToggle={sethasTreatmentError}
					/>
					<Box
						display='flex'
						justifyContent='flex-end'
						alignItems='center'
						mt='32px'
						gap='16px'
					>
						<TypographySecondary
							sx={{ cursor: 'pointer' }}
							onClick={() => {
								!isEqualState
									? setExitDialogOpen(true)
									: leaveCheckupPage(navigate)
							}}
						>
							Отменить
						</TypographySecondary>

						<ButtonUI disabled={!canSave} onClick={handleSaveCheckup}>
							Сохранить
						</ButtonUI>
					</Box>
				</>
			)}
			<ExitWarningModal
				open={exitDialogOpen}
				onClose={() => setExitDialogOpen(false)}
				onSave={() => setExitDialogOpen(false)}
				onDismiss={() => {
					setExitDialogOpen(false)
					leaveCheckupPage(navigate)
				}}
				title={'Выйти без сохранения'}
				description={'Внесенные изменения будут утеряны.'}
				onSaveText={'Вернуться к редактированию'}
				onDismissText={'Выйти без сохранения'}
			/>
		</PageContainer>
	)
}

export default CheckUpPage
